/**
 * The DESK primitives that genuinely need a browser — kept OUT of `./desk.tsx`
 * so importing a table or a page head never drags a client bundle onto a
 * zero-JS board. Plan §4,
 * `docs/plans/2026-08-23-supply-desk-elevated-rollout.md`.
 *
 * Three things live here and nothing else: a drill-in Drawer, a toast that is
 * anchored to the DESK rather than to a tab bar that is `display:none` at
 * ≥1024px, and the controlled search input a client board puts inside
 * `FilterBar`. If a new primitive can be written without state, it belongs in
 * the sibling file.
 */
"use client";

import { useCallback, useEffect, useId, useRef, type ReactNode } from "react";
import { Search, X } from "lucide-react";

/* ── drawer ────────────────────────────────────────────────────────────── */

/**
 * The right-side drill-in panel. Extracted from hygiene's `DrawerShell`
 * (`HygieneQueue.tsx:364`), which is the app's proven shape — a bottom sheet
 * under `sm`, a full-height 560px panel above it — with the three things the
 * donor never had:
 *
 *   · **Escape closes it.** A modal a keyboard cannot dismiss is a trap, and
 *     every desk reader has a keyboard.
 *   · **Focus moves in and comes back.** On open the close button takes focus
 *     (so the next Tab lands inside the panel, not behind the scrim); on close
 *     focus returns to whatever opened it, or a drill-in costs the reader
 *     their place in a 500-row table.
 *   · **The scrim is a real close target**, and the panel stops the click from
 *     reaching it.
 *
 * `absolute inset-0` like the donor, not `fixed`: the rail and tab bar are
 * `absolute` by shell contract (`supply-shell-guard`), and a `fixed` overlay
 * would sit over the rail.
 *
 * 🔴 The containing block is the SUPPLY SHELL ROOT — `SupplyShell.tsx:99`,
 * `supply-shell relative flex h-dvh w-full flex-col overflow-hidden` — and
 * that is the whole reason hygiene's drawer works. Do NOT add `relative` to
 * the page's `<main>` to "give this something to anchor to": `<main>` is
 * `SUPPLY_SCROLL_MAIN`, i.e. the scroller, and an `absolute inset-0` overlay
 * inside a SCROLLING positioned ancestor pins to that ancestor's content
 * origin, not to the visible area — open the drawer 800px down a 500-row
 * table and the panel would sit 800px above the fold. Anchored to the shell
 * root, `inset-0` IS the viewport, at any scroll position.
 */
export function Drawer({ title, subtitle, icon, tone = "neutral", onClose, footer, children }: {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  /** `danger` only when the panel's own action destroys or reverses something. */
  tone?: "neutral" | "danger";
  onClose: () => void;
  /** Pinned action row at the bottom of the panel — the confirm lives here, never below the fold. */
  footer?: ReactNode;
  children: ReactNode;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const returnRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  useEffect(() => {
    returnRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    return () => { returnRef.current?.focus?.(); };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return <div role="presentation" onClick={onClose} className="absolute inset-0 z-40 flex items-end justify-end bg-black/55 sm:items-stretch">
    <section
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={(event) => event.stopPropagation()}
      className="flex max-h-full w-full flex-col rounded-t-[22px] border border-[var(--border)] bg-[var(--inner)] shadow-2xl sm:w-[560px] sm:rounded-none"
    >
      <div className="flex items-start gap-3 p-4 sm:p-6">
        {icon ? <span className={`flex size-11 shrink-0 items-center justify-center rounded-[14px] ${tone === "danger" ? "bg-[var(--danger-well)] text-[var(--danger)]" : "bg-[var(--raised)] text-[var(--muted)]"}`}>{icon}</span> : null}
        <div className="min-w-0 flex-1">
          <h2 id={titleId} className="text-[19px] font-extrabold text-[var(--text)]">{title}</h2>
          {subtitle ? <p className="mt-1 text-[13px] font-bold leading-5 text-[var(--muted)]">{subtitle}</p> : null}
        </div>
        <button ref={closeRef} type="button" onClick={onClose} aria-label={`Close ${title}`} className="flex size-11 shrink-0 items-center justify-center rounded-[12px] border border-[var(--border)] bg-[var(--surface)] text-[var(--muted)]"><X className="size-4" aria-hidden /></button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 sm:px-6 sm:pb-6">{children}</div>
      {footer ? <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-[var(--border-soft)] bg-[var(--surface)] px-4 py-3 sm:px-6">{footer}</div> : null}
    </section>
  </div>;
}

/* ── toast ─────────────────────────────────────────────────────────────── */

/**
 * The desk-anchored toast.
 *
 * ApprovalCard and PoSendRail pin theirs at `bottom-[82px]` — 82px of
 * clearance for a tab bar that is `display:none` above 1024px, so on a desk
 * every toast floats in dead space. This offsets by `--supply-tab-clearance`
 * instead, which IS the bar's height on a phone and collapses to the
 * safe-area inset at desk width — one expression, correct at both.
 *
 * `role="status"` + `aria-live="polite"`: a result announcement, never an
 * alert. It does not steal focus, so it must never be the only place a
 * failure is reported.
 */
export function Toast({ children, tone = "neutral", onDismiss, dismissLabel = "Dismiss", className = "" }: { children: ReactNode; tone?: "neutral" | "success" | "danger"; onDismiss?: () => void; dismissLabel?: string; className?: string }) {
  const tones = {
    neutral: "bg-[var(--text)] text-[var(--surface)]",
    success: "bg-[var(--success)] text-white",
    danger: "bg-[var(--danger)] text-white",
  } as const;
  return <div
    role="status"
    aria-live="polite"
    className={`pointer-events-auto fixed inset-x-4 bottom-[calc(var(--supply-tab-clearance)+16px)] z-50 mx-auto flex max-w-sm items-center gap-3 rounded-[14px] px-4 py-3 text-[13px] font-bold shadow-2xl lg:inset-x-auto lg:right-6 lg:mx-0 ${tones[tone]} ${className}`}
  >
    <span className="min-w-0 flex-1">{children}</span>
    {onDismiss ? <button type="button" onClick={onDismiss} aria-label={dismissLabel} className="-my-2 -mr-1.5 flex size-11 shrink-0 items-center justify-center rounded-[10px] opacity-80"><X className="size-4" aria-hidden /></button> : null}
  </div>;
}

/* ── search field ──────────────────────────────────────────────────────── */

/**
 * The controlled search input for a client board's `FilterBar`. 44px against
 * the phone kit's 48px — a filter bar is chrome and the plan's move 5 says
 * chrome earns its height, but 44 is the floor, not a preference: it is also
 * the height its own clear button needs (`__tests__/touch-target-floor`, which
 * caught this file at 28px). A shorter field means either a shorter clear
 * button or one that overflows its own box.
 *
 * The unprefixed `text-[16px]` is not a style choice: under 16px iOS zooms the
 * page on focus and stays zoomed (`__tests__/ios-input-zoom-guard`). The 14px
 * desk size rides an `lg:` prefix, which the guard allows because ≥1024px is
 * not a phone.
 */
export function DeskSearchField({ value, onChange, placeholder, label, className = "" }: { value: string; onChange: (next: string) => void; placeholder: string; label: string; className?: string }) {
  const clear = useCallback(() => onChange(""), [onChange]);
  return <label className={`flex h-11 min-w-[200px] flex-1 items-center gap-1 rounded-[12px] border border-[var(--border)] bg-[var(--surface)] pl-3 pr-1 focus-within:border-[var(--accent)] ${className}`}>
    <Search className="size-4 shrink-0 text-[var(--muted)]" strokeWidth={2.4} aria-hidden />
    <input type="search" value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} aria-label={label} className="h-full w-full min-w-0 bg-transparent text-[16px] font-semibold text-[var(--text)] placeholder:text-[var(--faint)] focus:outline-none lg:text-[14px]" />
    {value ? <button type="button" onClick={clear} aria-label={`Clear ${label}`} className="flex size-11 shrink-0 items-center justify-center rounded-[10px] text-[var(--muted)]"><X className="size-4" aria-hidden /></button> : null}
  </label>;
}
